/**
 * SunoMashupSheet — выбор второго трека для mashup.
 *
 * Открывается из CardModal для уже выбранного трека (source). Показывает
 * список остальных треков юзера из библиотеки. Пользователь выбирает
 * второй — жмёт «Смикшировать». Запускаем `generateMashup({track_ids: [...]})`,
 * polling, toast, refresh библиотеки.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useCreditsGuard } from '../../../hooks/useCreditsGuard'
import CreditsShortNote from '../../../components/CreditsShortNote'
import Sheet from '../../../components/Sheet'
import Loader from '../../../components/ui/Loader'
import { Icon } from '../../../../public/icons/Icon.tsx'
import { showToast } from '../../../components/Toast'
import { getUserTracks, generateMashup, type SunoTrack } from '../../../api/music'
import { useSunoPolling } from '../hooks/useSunoPolling'
import { useProviders } from '../../../hooks/useProviders'
import { translateSunoError } from '../utils/sunoErrors'
import TokenPrice from '../../../components/TokenPrice'

interface Props {
	track: SunoTrack
	open: boolean
	onClose: () => void
	onCompleted?: () => void
	/** Если передан — используем как источник треков, без доп. запроса.
	 *  Типично — из BotsAudioLibrary где треки уже загружены. */
	tracksCache?: SunoTrack[]
}

const LIST_LIMIT = 50

export default function SunoMashupSheet({ track, open, onClose, onCompleted, tracksCache }: Props) {
	const guard = useCreditsGuard()
	const [fetchedTracks, setFetchedTracks] = useState<SunoTrack[]>([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [selectedId, setSelectedId] = useState<string | null>(null)
	const polling = useSunoPolling()
	const { brands } = useProviders()

	// Если родитель передал готовый список — используем его. Иначе — fetch.
	const tracks = useMemo(
		() => (tracksCache ?? fetchedTracks).filter((t) => t.id !== track.id),
		[tracksCache, fetchedTracks, track.id],
	)

	const price = (() => {
		for (const brand of brands) {
			for (const bm of brand.models) {
				if (bm.type === 'audio' && bm.variants && bm.id.toLowerCase().startsWith('suno')) {
					const v = bm.variants.find((x) => x.key === 'mashup')
					if (v?.credits_price != null) return v.credits_price
				}
			}
		}
		return null
	})()

	// Загружаем треки только если cache не передан. При закрытии и повторном
	// открытии сбрасываем выбор, но cache сохраняется → нет лишнего HTTP.
	useEffect(() => {
		if (!open) return
		// eslint-disable-next-line react-hooks/set-state-in-effect -- шторка открылась заново: выбор прошлого раза сброшен
		setSelectedId(null)
		if (tracksCache) return // есть готовый список — ничего не делаем

		setLoading(true)
		setError(null)
		getUserTracks({ operation: 'generate', limit: LIST_LIMIT })
			.then(({ tracks }) => setFetchedTracks(tracks))
			.catch((err) => setError(err instanceof Error ? err.message : 'Ошибка загрузки'))
			.finally(() => setLoading(false))
	}, [open, tracksCache])

	const handleSubmit = useCallback(async () => {
		if (!selectedId || polling.status === 'running') return
		showToast('Mashup — запущен', 'info')
		const res = await polling.run(() =>
			generateMashup({ track_ids: [track.id, selectedId] }),
		)
		if (res) {
			showToast('Mashup: готово', 'success')
			onCompleted?.()
			onClose()
		} else if (polling.error) {
			showToast(translateSunoError(polling.error), 'error')
		}
	}, [selectedId, polling, track.id, onCompleted, onClose])

	const isRunning = polling.status === 'running'

	return (
		<Sheet
			open={open}
			onClose={onClose}
			variant="center"
			title="Mashup — миксуем два трека"
			className="suno-mashup-sheet"
		>
			<div className="suno-mashup-sheet__body">
				<div className="suno-mashup-sheet__source">
					<div className="suno-mashup-sheet__source-label">Первый трек</div>
					<div className="suno-mashup-sheet__source-title">
						<Icon id="gen-audio" size={14} />
						{track.title?.trim() || 'Без названия'}
					</div>
				</div>

				<div className="suno-mashup-sheet__section-label">
					Выбери второй трек из библиотеки:
				</div>

				{loading && (
					<div className="suno-mashup-sheet__loading">
						<Loader size="md" centered label="Загружаем треки..." />
					</div>
				)}

				{!loading && error && (
					<div className="suno-mashup-sheet__error">{error}</div>
				)}

				{!loading && !error && tracks.length === 0 && (
					<div className="suno-mashup-sheet__empty">
						В библиотеке только один трек — для mashup нужен второй.
					</div>
				)}

				{!loading && !error && tracks.length > 0 && (
					<div className="suno-mashup-sheet__list">
						{tracks.map((t) => {
							const isSelected = selectedId === t.id
							return (
								<button
									key={t.id}
									type="button"
									className={`suno-mashup-sheet__item${isSelected ? ' suno-mashup-sheet__item--selected' : ''}`}
									onClick={() => setSelectedId(t.id)}
									disabled={isRunning}
								>
									<Icon id="gen-audio" size={14} />
									<span className="suno-mashup-sheet__item-title">
										{t.title?.trim() || 'Без названия'}
									</span>
									{t.tags && (
										<span className="suno-mashup-sheet__item-tags">{t.tags}</span>
									)}
									{isSelected && (
										<span className="suno-mashup-sheet__item-check" aria-hidden="true">✓</span>
									)}
								</button>
							)
						})}
					</div>
				)}

				<div className="suno-mashup-sheet__footer">
					<button
						type="button"
						className="suno-mashup-sheet__cancel"
						onClick={onClose}
						disabled={isRunning}
					>
						Отмена
					</button>
					<button
						type="button"
						className="suno-mashup-sheet__submit"
						onClick={handleSubmit}
						disabled={!selectedId || isRunning || guard.shortFor(price)}
						title={price !== null && guard.shortFor(price) ? guard.titleFor(price) : undefined}
					>
						{isRunning && <span className="suno-inline-spinner" aria-hidden="true" />}
						{isRunning ? 'Миксуем...' : 'Смикшировать'}
						{price !== null && !isRunning && (
							<span className="suno-mashup-sheet__submit-price">
								<TokenPrice value={price} iconSize={12} />
							</span>
						)}
					</button>
					{guard.shortFor(price) && <CreditsShortNote price={price} />}
				</div>
			</div>
		</Sheet>
	)
}
